import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { TeamFormFieldSkeleton } from "./TeamLoadingSkeletons";
import TeamCreateAgentSelect from "./TeamCreateAgentSelect";
import {
  peopleSelectControlSx,
  peopleSelectSx,
  teamGroupSelectMenuProps,
} from "./teamFormStyles";
import {
  fieldErrorSx,
  hasValidationErrors,
  peopleFieldMaxLengthProps,
  peopleFormErrorTextSx,
  validateTeamForm,
} from "../peopleFormValidation";

const fieldLabelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  mb: 0.75,
};

const inputSx = {
  width: 360,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#FFF",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    fontFamily: "Inter",
    fontSize: 14,
    "& fieldset": { border: "1px solid #D5DCE1" },
    "&:hover fieldset": { border: "1px solid #D5DCE1" },
    "&.Mui-focused fieldset": { border: "1px solid #D5DCE1" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 1.5,
    boxSizing: "border-box",
    "&::placeholder": { color: "#C5CED6", opacity: 1 },
  },
};

const singleLineInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    height: 40,
  },
  "& .MuiOutlinedInput-input": {
    ...inputSx["& .MuiOutlinedInput-input"],
    height: 40,
  },
};

const textareaSx = {
  ...inputSx,
  padding: "0px",
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    minHeight: 96,
    maxHeight: 200,
    alignItems: "flex-start",
    py: 1,
    overflow: "auto",
  },
  "& .MuiOutlinedInput-input": {
    ...inputSx["& .MuiOutlinedInput-input"],
    height: "auto !important",
    minHeight: 72,
    padding: "0px",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  "& textarea": {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
};

const selectSx = peopleSelectControlSx({ width: 360, maxWidth: "100%" });

const EMPTY_FORM = { name: "", description: "", groupId: "", memberIds: [] };
const EMPTY_ERRORS = { name: "", description: "", groupId: "" };

export default function TeamCreateModal({
  open,
  onClose,
  onConfirm,
  groupOptions = [],
  agentOptions = [],
  optionsLoading = false,
  submitting = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const selectedGroupLabel = groupOptions.find((g) => g.id === form.groupId)?.label;

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors(EMPTY_ERRORS);
    }
  }, [open]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = () => {
    const nextErrors = validateTeamForm(form);
    if (hasValidationErrors(nextErrors)) {
      setErrors(nextErrors);
      return;
    }
    onConfirm({
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
    });
    // setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2.5,
          }}
        >
          Create Team
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Team Name</Typography>
            <TextField
              fullWidth
              placeholder="Enter team name"
              value={form.name}
              onChange={set("name")}
              error={Boolean(errors.name)}
              sx={{ ...singleLineInputSx, ...fieldErrorSx(errors.name) }}
              {...peopleFieldMaxLengthProps("teamName")}
            />
            {errors.name ? <Typography sx={peopleFormErrorTextSx}>{errors.name}</Typography> : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Description</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder="Team description and responsibilities"
              value={form.description}
              onChange={set("description")}
              error={Boolean(errors.description)}
              sx={{ ...textareaSx, ...fieldErrorSx(errors.description) }}
              {...peopleFieldMaxLengthProps("teamDescription")}
            />
            {errors.description ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.description}</Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Group</Typography>
            {optionsLoading ? (
              <TeamFormFieldSkeleton />
            ) : (
              <>
                <FormControl
                  size="small"
                  error={Boolean(errors.groupId)}
                  sx={{ ...selectSx, ...fieldErrorSx(errors.groupId) }}
                >
                  <Select
                    displayEmpty
                    value={form.groupId}
                    onChange={set("groupId")}
                    IconComponent={Dropdown}
                    MenuProps={teamGroupSelectMenuProps({ placement: "top" })}
                    sx={peopleSelectSx}
                    renderValue={() => (
                      <Typography
                        component="span"
                        noWrap
                        sx={{
                          color: selectedGroupLabel ? "#061522" : "#C5CED6",
                          fontSize: 14,
                          fontFamily: "Inter",
                          fontWeight: 400,
                        }}
                      >
                        {selectedGroupLabel || "Select group"}
                      </Typography>
                    )}
                  >
                    {groupOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.groupId ? (
                  <Typography sx={peopleFormErrorTextSx}>{errors.groupId}</Typography>
                ) : null}
              </>
            )}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Agents</Typography>
            <TeamCreateAgentSelect
              agentOptions={agentOptions}
              loading={optionsLoading}
              selectedIds={form.memberIds}
              onChange={(memberIds) => {
                setForm((prev) => ({ ...prev, memberIds }));
                setErrors((prev) => ({ ...prev, memberIds: "" }));
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || optionsLoading}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              background: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#0077CC" },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {submitting ? "Creating…" : "Create Team"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
